import _ from 'lodash';
import * as Yup from 'yup';
import { useAppSelector } from '@/shared/hooks/store';
import { selectOnboardingUser } from '@/store/slices/onboarding/selectors';
import { Button, Modal } from '@/shared/ui';
import { ITestClaimTransferPrescriptions } from '@/shared/types/interfaces/transfer-prescriptions';
import MedicationQuantityFrequency from '../../../medbox/shared/medication-quantity';
import { IMedicationQuantityInitialValues } from '../../../../../../shared/constants/medication-quantity';
import { ERROR_MESSAGES } from '@/shared/constants/error-messages';
import { copayEligiblePotencyCodes } from '../../../../../../shared/constants/potency-code';
import { UnitMedicationForm } from '../../../medbox/components/medications/unit-medication-form';

import './style.css';
import { useFormikWrapper } from '@/shared/hooks/formikWrapper';
import { useMemo } from 'react';

interface IProps {
    medication: ITestClaimTransferPrescriptions;
    onClose(): void;
    onSubmit(values: IMedicationQuantityInitialValues): void;
}

export const MedicalCardEditQuantity = ({
    medication,
    onClose,
    onSubmit,
}: IProps) => {

    const isPillOrCapsule = useMemo(() => {
        return copayEligiblePotencyCodes.has(medication.prescribable.potencyCode);
    }, [medication]);
    // here we will have to do some work... 
    const validationSchema = Yup.object().shape({
        fillFrequency: Yup.number().required(ERROR_MESSAGES.COMPLETE_EACH_SECTION),
        quantity: Yup.number()
            .required(ERROR_MESSAGES.COMPLETE_EACH_SECTION)
            .when('fillFrequency', (relevantArgs, schema) => {
                const maxQuantity = relevantArgs[0] === 90 ? 2880 : 960;
                return schema.max(maxQuantity, `Quantity cannot exceed ${maxQuantity} for fill frequency of ${relevantArgs[0]} days`);
            }),
    });
    const user = useAppSelector(selectOnboardingUser)!;
    const userHasSelectedBothCycleDurations = ['both'].includes(user?.userOnboarding?.details?.cycleDuration || '');
    
    const formik = useFormikWrapper({
        initialValues: {
            quantity: medication.copayQuantity,
            fillFrequency: !userHasSelectedBothCycleDurations ? Number(user?.userOnboarding?.details?.cycleDuration) : medication.copayDaysSupply
        },
        validationSchema,
        onSubmit: (values) => {
            onSubmit(values)
        }
    });

    const validationSchemaUnit = Yup.object().shape({
        quantity: Yup.number()
            .required(ERROR_MESSAGES.COMPLETE_EACH_SECTION)
            .when('fillFrequency', (relevantArgs, schema) => {
                const maxQuantity = relevantArgs[0] === 90 ? 2880 : 960;
                return schema.max(maxQuantity, `Quantity cannot exceed ${maxQuantity} for fill frequency of ${relevantArgs[0]} days`);
            }),
    });
    
    const unitFormik = useFormikWrapper({
        initialValues: {
            quantity: medication.copayQuantity,
        },
        validationSchema: validationSchemaUnit,
        onSubmit: (values) => {
            onSubmit({ ...values, fillFrequency: 30 })
        }
    });


    const correctFormik = useMemo(() => {
        return isPillOrCapsule ? formik : unitFormik;
    }, [isPillOrCapsule, formik, unitFormik]);


    const quantityComponent = useMemo(() => {
        if (isPillOrCapsule) {
            return (
                <MedicationQuantityFrequency
                    prescriptionFlowFlag
                    formik={formik}
                    rxShowHowOftenFill={userHasSelectedBothCycleDurations}
                />
            );
        } else {
            return (
                <UnitMedicationForm
                    medication={medication.prescribable}
                    isSubmittingEditQuantityOnly
                    formik={unitFormik}
                    hideButtons
                />
            );
        }
    }, [isPillOrCapsule, formik, userHasSelectedBothCycleDurations, medication.prescribable, unitFormik]);


    return (
        <Modal
            dataTestId="medical-card-edit-quantity"
            defaultPaddings={false}
            open={true}
            onClose={onClose}
            contentClassName="!max-w-[490px] px-[1.25rem] py-[1.5625rem] sm:p-[2.5rem]"
        >
            <form className="flex flex-col items-center justify-center" data-testid="confirm-modal-container" onSubmit={correctFormik.handleSubmit}>
                <div className="flex flex-col gap-[0.4375rem] sm:gap-[0.3125rem] mb-[0.9375rem]">
                    <h2 className='font-agrandir text-25 sm:text-35 text-center'>Edit Quantity</h2>
                    <span className='font-atkinsonHyperlegible text-18 sm:text-20 text-center'>Please enter the correct quantity for <span className="ph-no-capture">{medication.prescribable.name}</span></span>
                </div>
                <div className="flex flex-col gap-[1.25rem] sm:gap-[1.5625rem] w-full">
                    {quantityComponent}
                    <Button
                        testId="button-confirm-modal-confirm"
                        className={`confirm_modal__confirm`}
                        type="submit"
                        sx={{ width: '100%', fontSize: '1.125rem' }}
                        showEndIcon={true}
                        loading={false}
                        disabled={false}
                    >
                        <span className="font-dmSans">Save Quantity</span>
                    </Button>
                    <Button
                        testId="button-confirm-modal-cancel"
                        className='confirm_modal__cancel'
                        fontClassName='underline font-dmSansMedium text-17 sm:text-18 font-bold'
                        sx={{ width: '100%', fontSize: '1.125rem' }}
                        variant="text"
                        onClick={onClose}
                    >
                        <span>Keep Current Quantity</span>
                    </Button>
                </div>
            </form>
        </Modal>
    );
};